import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import i18next from 'i18next';
import sites from 'src/js/modules/sites';
import dialogs from 'src/js/modules/dialogs';
import status from 'src/js/modules/status';
import user from 'src/js/modules/user';
import config from 'src/js/modules/config';
import useAvailablePlatformComponents from 'src/js/modules/user/hooks/use-available-platform-components';
import useFeatureFlags from 'src/js/modules/feature-flags/hooks/use-feature-flags';
import segment from 'src/js/modules/analytics/segment';
import useStaticAsset from 'src/js/common/hooks/use-static-asset';
import routesMap from 'src/js/router/helpers/routes-map';
import legacyUrlMap from 'src/js/router/helpers/legacy-urls-map';
import subscriptions from '../../subscriptions';
import { SiteList, SiteListItem } from '../components/site-list';
import { Site, SiteSkeleton } from '../components/site';
import generatePublishUrl from '../helpers/generate-publish-url';
import SiteCreation from '../components/site-creation';
import SiteListError from '../components/site-list-error';
import DomainSearch from '../components/domain-search';
import redirectToUrl from '../../../utils/redirect-to-url';

const { platformComponents } = user.constants;
const { constants } = status;
const {
  constants: { triggerIds, events },
  track,
  trackAsync,
} = segment;

function getSkeletonItems() {
  return ['skeleton-0', 'skeleton-1'];
}

function SitesListContainer({ onAddNewSiteClick }) {
  const navigate = useNavigate();

  const sitesList = useSelector(sites.selectors.getSites, shallowEqual);
  const sitesStatus = useSelector(sites.selectors.getSitesStatus);
  const contactSupportUrl = useSelector(user.selectors.getContactSupportUrl);
  const domainPurchaseAvailable = useSelector(user.selectors.getDomainPurchaseAvailable);

  const dispatch = useDispatch();
  const availablePlatformComponents = useAvailablePlatformComponents();
  const siteExampleImage = useStaticAsset('/img/site-example-brandless.png');
  const [featureFlags] = useFeatureFlags(['legacy_latitude_templates_eol']);
  const { legacy_latitude_templates_eol: isLegacyLatitudeTemplatesEolEnabled } = featureFlags;

  const yolaUrl = useSelector(config.selectors.getYolaUrl);
  const legacyEolBlogUrl = useMemo(() => {
    const url = new URL(yolaUrl);
    url.pathname = '/blog/upcoming-end-of-life-of-legacy-website-architecture-in-sitebuilder/';
    return url.toString();
  }, [yolaUrl]);

  const onBuyDomain = useCallback(
    (domainName) => {
      track(events.BUY_A_DOMAIN_TRIGGER_CLICKED, {
        triggerId: triggerIds.SECURE_DOMAIN,
      });

      navigate(
        routesMap.domainSelector.url({
          query: { prefill: domainName, triggerId: triggerIds.SECURE_DOMAIN },
        })
      );
    },
    [navigate]
  );

  const onDomainSearchSubmit = useCallback(
    (domainName) => {
      track(events.DASHBOARD_DOMAIN_SEARCH_CLICKED, {
        domainName: domainName || null,
      });

      navigate(
        routesMap.domainSelector.url({
          query: { prefill: domainName, triggerId: triggerIds.DASHBOARD_DOMAIN_SEARCH },
        })
      );
    },
    [navigate]
  );

  const onDomainSettingsClick = useCallback(async (siteData) => {
    const { hasCustomDomain: customDomain = false } = siteData;
    const activeDomainSubscriptions = subscriptions.accessors.getActiveDomainSubscriptions();
    const domainsUrl = siteData.isWs
      ? legacyUrlMap.domains()
      : legacyUrlMap.legacyDomainsAdvanced(siteData.sitebuilderUrl);

    await trackAsync(events.DOMAIN_SETTINGS_TRIGGER_CLICKED, {
      customDomain,
      hasActiveDomainSubscription: Boolean(activeDomainSubscriptions.length),
    });

    redirectToUrl(domainsUrl);
  }, []);

  const closeDialog = useCallback(() => {
    dispatch(dialogs.actions.hide());
  }, [dispatch]);

  const onExportSite = useCallback(
    (siteId) => {
      dispatch(
        dialogs.actions.show(dialogs.dialogTypes.PENDING, {
          width: 380,
          height: 200,
          captions: {
            title: `${i18next.t('Preparing a ZIP')}.`,
            description: i18next.t('Your site archive will be generated soon'),
          },
        })
      );

      dispatch(
        sites.thunks.exportSite(siteId, {
          includeMeta: true,
        })
      )
        .then((response) => {
          const { url: exportDataUrl } = response;

          dispatch(
            dialogs.actions.show(dialogs.dialogTypes.EXPORT_INFORMATION_DIALOG, {
              siteArchiveLink: exportDataUrl,
              onDismiss: () => dispatch(dialogs.actions.hide()),
            })
          );
        })
        .catch(() => {
          dispatch(
            dialogs.actions.show(dialogs.dialogTypes.LEGACY_SITE_ERROR_DIALOG, {
              onDismiss: closeDialog,
            })
          );
        });
    },
    [dispatch, closeDialog]
  );

  const userSites = useMemo(() => {
    switch (sitesStatus) {
      case constants.LOADING:
        return getSkeletonItems().map((item) => (
          <SiteListItem key={item}>
            <SiteSkeleton />
          </SiteListItem>
        ));
      case constants.FAILED:
        return [
          <SiteListItem key="load-failed">
            <SiteListError contactSupportUrl={contactSupportUrl} />
          </SiteListItem>,
        ];

      default: {
        if (sitesList.length) {
          return sitesList.map((siteData) => (
            <SiteListItem key={siteData.id}>
              <Site
                availablePlatformComponents={availablePlatformComponents}
                siteData={siteData}
                publishSiteUrl={siteData.isWs ? generatePublishUrl(siteData.sitebuilderUrl) : null}
                onPreviewClick={() => redirectToUrl(siteData.sitebuilderUrl)}
                onDelete={() => {
                  dispatch(
                    dialogs.actions.show(dialogs.dialogTypes.DELETE_SITE, {
                      siteId: siteData.id,
                    })
                  );
                }}
                onReset={() => {
                  dispatch(
                    dialogs.actions.show(dialogs.dialogTypes.RESET_SITE, {
                      siteId: siteData.id,
                      isPublished: siteData.isPublished,
                      templateBuildSlug: siteData.templateBuildSlug,
                    })
                  );
                }}
                onUnpublish={() => {
                  dispatch(
                    dialogs.actions.show(dialogs.dialogTypes.UNPUBLISH_SITE, {
                      siteId: siteData.id,
                    })
                  );
                }}
                onExportSite={() => onExportSite(siteData.id)}
                onBuyDomain={onBuyDomain}
                onDomainSettingsClick={onDomainSettingsClick}
                eolLearnMoreUrl={legacyEolBlogUrl}
                isLegacyLatitudeTemplatesEolEnabled={isLegacyLatitudeTemplatesEolEnabled}
              />
            </SiteListItem>
          ));
        }

        return [
          <SiteListItem key="no-sites">
            <SiteCreation imageUrl={siteExampleImage} onAddNewSiteClick={onAddNewSiteClick} />
          </SiteListItem>,
        ];
      }
    }
  }, [
    sitesList,
    availablePlatformComponents,
    dispatch,
    sitesStatus,
    contactSupportUrl,
    siteExampleImage,
    onBuyDomain,
    onAddNewSiteClick,
    onDomainSettingsClick,
    legacyEolBlogUrl,
    onExportSite,
    isLegacyLatitudeTemplatesEolEnabled,
  ]);

  const userSitesWithDomainSearch = useMemo(() => {
    if (
      !availablePlatformComponents.includes(platformComponents.DOMAINS) ||
      !domainPurchaseAvailable
    ) {
      return userSites;
    }

    return [
      ...userSites.slice(0, 2),
      <SiteListItem key="domain-search">
        <DomainSearch onSubmit={onDomainSearchSubmit} />
      </SiteListItem>,
      ...userSites.slice(2),
    ];
  }, [userSites, availablePlatformComponents, onDomainSearchSubmit, domainPurchaseAvailable]);

  return (
    <SiteList
      noSites={
        (!sitesList.length && sitesStatus === constants.SUCCEEDED) ||
        sitesStatus === constants.FAILED
      }
    >
      {userSitesWithDomainSearch}
    </SiteList>
  );
}

SitesListContainer.propTypes = {
  onAddNewSiteClick: PropTypes.func.isRequired,
};

export default SitesListContainer;
